import React from "react";

// ✅ Unified profile component (Agent / Scout / Club)
import RoleProfile from "./RoleExtras/RoleProfile";

// ✅ Unified verification component (Agent / Scout / Club)
import RoleVerification from "./RoleExtras/RoleVerification";

// ✅ NEW: Feed / FYP page
import Feed from "./RoleExtras/Feed";

// ✅ Shared “junction” PlayerProfile page (lives in PlayerDashboard/PlayerExtras)
import PlayerProfile from "../PlayerDashboard/PlayerExtras/PlayerProfile";

// ✅ FULL PAGE VideoProfile rendered in <Outlet />
// NOTE: adjust this import path ONLY if your folder structure differs.
import PlayerVideoProfile from "../PlayerDashboard/PlayerVideoProfile";

// Shared, role-aware pages
import PostOpportunity from "./PostOpportunity";
import ViewPosts from "./RoleExtras/ViewPosts";
import ViewApplications from "./RoleExtras/ViewApplications";
import Bookmarks from "./RoleExtras/Bookmarks";
import OffertoSignPlayer from "./RoleExtras/OffertoSignPlayer";

// VSN Transfers
import ContractOffer from "./RoleExtras/ContractOffer";
import ManageContract from "./RoleExtras/ManageContract";
import ShowInterest from "./RoleExtras/ShowInterest";

// Notifications
import Notifications from "./RoleExtras/Notifications";

// ✅ ROLE-AWARE EXTRAS
import RoleChats from "./RoleExtras/RoleChats";
import RoleReport from "./RoleExtras/RoleReport";
import VSNWorld from "./RoleExtras/VSNWorld";

// =========================
// Shared dashboard extras
// =========================
const baseExtras = [
    // ✅ NEW: Feed route (works for agent/scout/club)
    // URLs:
    //   /agent-dashboard/feed
    //   /scout-dashboard/feed
    //   /club-dashboard/feed
    { path: "feed", label: "Feed", element: <Feed /> },

    { path: "notifications", label: "Notifications", element: <Notifications /> },
    { path: "chats", label: "Chats", element: <RoleChats /> },

    // ✅ Keep PlayerProfile INSIDE the dashboard route tree
    // URL: /agent-dashboard/player-profile/:profileSlug (same for scout/club)
    // IMPORTANT: param name MUST match PlayerProfile.js useParams() (profileSlug)
    {
        path: "player-profile/:profileSlug",
        label: "Player Profile",
        element: <PlayerProfile />,
    },

    /**
     * ✅ Watch Player Videos URL (FULL PAGE)
     * IMPORTANT: keep this param consistent with your VideoProfile component.
     * In your App.js you have /video-profile/:playerId, so use :playerId here too.
     *
     * URLs:
     *   /agent-dashboard/video-profile/:playerId
     *   /scout-dashboard/video-profile/:playerId
     *   /club-dashboard/video-profile/:playerId
     */
    {
        path: "video-profile/:playerId",
        label: "Video Profile",
        element: <PlayerVideoProfile />,
    },

    { path: "manage-contract", label: "Manage Contract", element: <ManageContract /> },
    { path: "report", label: "Report", element: <RoleReport /> },
    { path: "show-interest", label: "Show Interest", element: <ShowInterest /> },
    { path: "vsn-world", label: "VSN World", element: <VSNWorld /> },
    { path: "bookmarks", label: "Bookmarks", element: <Bookmarks /> },

    {
        path: "offer-to-sign/:playerId",
        label: "Offer To Sign Player",
        element: <OffertoSignPlayer />,
    },

    { path: "view-posts", label: "View Posts", element: <ViewPosts /> },

    // ✅ IMPORTANT: This is the "deeper" route that ViewPosts should navigate to.
    {
        path: "opportunity/applications/:id",
        label: "Opportunity Applications",
        element: <ViewApplications />,
    },

    // (Optional) if you still use /<role>-dashboard/view-applications somewhere else
    {
        path: "view-applications",
        label: "View Applications",
        element: <ViewApplications />,
    },

    { path: "contract-offer", label: "Contract Offer", element: <ContractOffer /> },
    { path: "post-opportunity", label: "Post Opportunity", element: <PostOpportunity /> },

    /**
     * ✅ IMPORTANT:
     * DO NOT add "video-profiles" route here if you already render <C.VideoProfile />
     * in the dashboard grid. Otherwise you'll have TWO VideoProfiles and you'll see
     * mount/unmount + refetch issues.
     *
     * Fullscreen should be handled by the SAME VideoProfile instance + CSS using the URL.
     */
];

// =========================
// PUBLIC PROFILE + ROLE ROUTES
// =========================
const registry = {
    agent: [
        {
            path: "agent-profile/:profileSlug",
            label: "Profile",
            element: <RoleProfile roleOverride="agent" />,
        },
        {
            path: "verification",
            label: "Verification",
            element: <RoleVerification role="agent" />,
        },
        ...baseExtras,
    ],

    scout: [
        {
            path: "scout-profile/:profileSlug",
            label: "Profile",
            element: <RoleProfile roleOverride="scout" />,
        },
        {
            path: "verification",
            label: "Verification",
            element: <RoleVerification role="scout" />,
        },
        ...baseExtras,
    ],

    club: [
        {
            path: "club-profile/:profileSlug",
            label: "Profile",
            element: <RoleProfile roleOverride="club" />,
        },
        {
            path: "verification",
            label: "Verification",
            element: <RoleVerification role="club" />,
        },
        ...baseExtras,
    ],
};

// =========================
// Utility
// =========================
export default function getRoutesForRole(role) {
    return registry[role] || registry.agent;
}


























